/**
 * Utilitário centralizado de retry para pipelines do CorelDRAW.
 * 
 * Este módulo fornece funções reutilizáveis para executar etapas de pipeline
 * com retry automático, backoff exponencial e validação de resultados.
 * 
 * Features:
 * - Retry automático com backoff exponencial
 * - Validação customizada de resultados
 * - Logs integrados com sendToTerminal e pipelineLogger
 * - Suporte a abortSignal
 * - Métricas de tempo por tentativa
 */

import { sendToTerminal } from '../../terminalLogger';
import { logPipelineError } from '../logging/pipelineLogger';
import { pipelineSleep } from './pipelineConstants';

export interface PipelineRetryOptions {
  /** Número máximo de tentativas (padrão: 3) */
  maxRetries?: number;
  /** Delay base entre tentativas em ms (padrão: 1000ms) */
  baseDelayMs?: number;
  /** Delay máximo entre tentativas em ms (padrão: 10000ms) */
  maxDelayMs?: number;
  /** Fator de backoff exponencial (padrão: 2) */
  backoffFactor?: number;
  /** Função para validar se o resultado é válido (retorna true se válido) */
  validateResult?: (result: any) => boolean;
  /** Função para determinar se um erro deve ser retentado (retorna true se deve retry) */
  shouldRetry?: (error: any) => boolean;
  /** Callback chamado antes de cada retry */
  onRetry?: (attempt: number, error: any) => void;
  /** Callback chamado quando todas as tentativas falharem */
  onAllFailed?: (lastError: any, attempts: number) => void;
  /** AbortSignal para cancelar operação */
  abortSignal?: AbortSignal | null;
  /** Nome da etapa para logs (padrão: 'step') */
  stepName?: string;
  /** PipelineId para logs */
  pipelineId?: string;
}

export interface PipelineRetryResult<T> {
  /** Resultado da operação (se bem-sucedida) */
  result: T;
  /** Número de tentativas realizadas */
  attempts: number;
  /** Tempo total em ms */
  totalMs: number;
}

/**
 * Executa uma operação de pipeline com retry automático.
 * 
 * @param operation - Função assíncrona que executa a operação
 * @param options - Opções de configuração do retry
 * @returns Promise com resultado e métricas
 * @throws Error se todas as tentativas falharem
 */
export async function executePipelineStepWithRetry<T>(
  operation: () => Promise<T>,
  options: PipelineRetryOptions = {}
): Promise<PipelineRetryResult<T>> {
  const {
    maxRetries = 3,
    baseDelayMs = 1000,
    maxDelayMs = 10000,
    backoffFactor = 2,
    validateResult,
    shouldRetry,
    onRetry,
    onAllFailed,
    abortSignal,
    stepName = 'step',
    pipelineId
  } = options;

  const startedAt = Date.now();
  let lastError: any = null;
  let lastResult: T | null = null;
  let abortedEarly = false; // ✅ Flag para indicar se abortamos cedo por erro não retryable

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    // Verificar abortSignal antes de cada tentativa
    if (abortSignal?.aborted) {
      sendToTerminal('🛑 [PIPELINE_RETRY] Operação abortada', {
        stepName,
        pipelineId,
        attempt,
        maxRetries
      });
      throw new Error('Operação abortada pelo usuário');
    }

    // ✅ Definir attemptStartedAt ANTES do try para evitar erro no catch
    const attemptStartedAt = Date.now();
    
    try {
      sendToTerminal(`🔄 [PIPELINE_RETRY] Tentativa ${attempt}/${maxRetries} - ${stepName}`, {
        stepName,
        pipelineId,
        attempt,
        maxRetries
      });

      // Executar operação
      const result = await operation();
      lastResult = result;

      // Validar resultado se função de validação fornecida
      if (validateResult && !validateResult(result)) {
        const validationError = new Error(`Validação falhou para ${stepName}`);
        lastError = validationError;
        
        sendToTerminal(`⚠️ [PIPELINE_RETRY] Validação falhou - tentativa ${attempt}/${maxRetries}`, {
          stepName,
          pipelineId,
          attempt,
          maxRetries,
          result: result !== null && typeof result === 'object' ? Object.keys(result) : typeof result
        });

        // Se não for a última tentativa, fazer retry
        if (attempt < maxRetries) {
          const delayMs = Math.min(
            baseDelayMs * Math.pow(backoffFactor, attempt - 1),
            maxDelayMs
          );
          
          if (onRetry) {
            try {
              onRetry(attempt, validationError);
            } catch {}
          }

          sendToTerminal(`⏳ [PIPELINE_RETRY] Aguardando ${delayMs}ms antes de retry...`, {
            stepName,
            pipelineId,
            attempt,
            delayMs
          });

          await pipelineSleep(delayMs, `retry_${stepName}_${attempt}`);
          continue;
        } else {
          // Última tentativa falhou na validação
          break;
        }
      }

      // Sucesso!
      const attemptMs = Date.now() - attemptStartedAt;
      const totalMs = Date.now() - startedAt;

      sendToTerminal(`✅ [PIPELINE_RETRY] Sucesso na tentativa ${attempt}/${maxRetries} - ${stepName}`, {
        stepName,
        pipelineId,
        attempt,
        maxRetries,
        attemptMs,
        totalMs
      });

      return {
        result,
        attempts: attempt,
        totalMs
      };

    } catch (error) {
      lastError = error;
      const attemptMs = Date.now() - attemptStartedAt;
      
      // ✅ Preservar mensagem de erro customizado (MetadataError e subclasses)
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorName = error instanceof Error ? error.constructor.name : typeof error;

      sendToTerminal(`❌ [PIPELINE_RETRY] Falha na tentativa ${attempt}/${maxRetries} - ${stepName}`, {
        stepName,
        pipelineId,
        attempt,
        maxRetries,
        errorMessage,
        errorName,
        attemptMs,
        error: error instanceof Error ? error.message : String(error)
      });

      // ✅ CORRIGIDO: Verificar se erro deve ser retentado
      const canRetry = shouldRetry ? shouldRetry(error) : true; // Por padrão, sempre retry
      
      if (!canRetry) {
        sendToTerminal(`🛑 [PIPELINE_RETRY] Erro não retryable - abortando retries`, {
          stepName,
          pipelineId,
          attempt,
          errorMessage,
          errorName
        });
        // Não fazer retry - lançar erro imediatamente
        abortedEarly = true; // ✅ Marcar que abortamos cedo
        break;
      }

      // Se não for a última tentativa, fazer retry
      if (attempt < maxRetries) {
        const delayMs = Math.min(
          baseDelayMs * Math.pow(backoffFactor, attempt - 1),
          maxDelayMs
        );

        if (onRetry) {
          try {
            onRetry(attempt, error);
          } catch {}
        }

        sendToTerminal(`⏳ [PIPELINE_RETRY] Aguardando ${delayMs}ms antes de retry...`, {
          stepName,
          pipelineId,
          attempt,
          delayMs,
          nextAttempt: attempt + 1
        });

        await pipelineSleep(delayMs, `retry_${stepName}_${attempt}`);
      }
    }
  }

  // Todas as tentativas falharam OU erro não retryable abortou cedo
  const totalMs = Date.now() - startedAt;

  // ✅ CORRIGIDO: Só mostrar "Todas as tentativas falharam" se realmente esgotamos todas as tentativas
  if (!abortedEarly) {
    sendToTerminal(`💥 [PIPELINE_RETRY] Todas as tentativas falharam - ${stepName}`, {
      stepName,
      pipelineId,
      maxRetries,
      totalMs,
      lastError: lastError instanceof Error ? lastError.message : String(lastError)
    });

    if (pipelineId) {
      logPipelineError(pipelineId, stepName.toUpperCase(), {
        code: 'retry_exhausted',
        message: `Todas as ${maxRetries} tentativas falharam`,
        lastError: lastError instanceof Error ? lastError.message : String(lastError),
        attempts: maxRetries,
        totalMs
      });
    }
  } else {
    // ✅ Erro não retryable - não logar como "retry_exhausted"
    sendToTerminal(`🛑 [PIPELINE_RETRY] Erro não retryable detectado - ${stepName}`, {
      stepName,
      pipelineId,
      attempt: 1, // Primeira tentativa apenas
      totalMs,
      lastError: lastError instanceof Error ? lastError.message : String(lastError)
    });
  }

  if (onAllFailed) {
    try {
      onAllFailed(lastError, maxRetries);
    } catch {}
  }

  // ✅ Preservar erro customizado (MetadataError e subclasses) - não criar novo erro genérico
  // Isso mantém a mensagem amigável para o usuário
  if (lastError instanceof Error) {
    // Se for um erro customizado (tem propriedade 'code'), preservar como está
    if ('code' in lastError && typeof (lastError as any).code === 'string') {
      // Preservar erro customizado - já tem mensagem amigável
      sendToTerminal(`💥 [PIPELINE_RETRY] Preservando erro customizado: ${(lastError as any).code}`, {
        stepName,
        errorMessage: lastError.message,
        errorCode: (lastError as any).code
      });
      throw lastError;
    }
    
    // Para outros erros, criar erro com contexto mas preservar mensagem original
    const errorMessage = lastError.message || 'Erro desconhecido';
    const finalError = new Error(
      `${stepName} falhou após ${maxRetries} tentativas: ${errorMessage}`
    );
    
    // Preservar stack trace se disponível
    if (lastError.stack) {
      (finalError as any).originalError = lastError;
      (finalError as any).stack = lastError.stack;
    }
    
    throw finalError;
  }
  
  // Se não for Error, converter para string
  throw new Error(`${stepName} falhou após ${maxRetries} tentativas: ${String(lastError || 'Erro desconhecido')}`);
}

/**
 * Executa uma operação de pipeline com retry e retorna resultado ou null.
 * Útil quando queremos tratar falha como não-crítica.
 * 
 * @param operation - Função assíncrona que executa a operação
 * @param options - Opções de configuração do retry
 * @returns Promise com resultado ou null se todas as tentativas falharem
 */
export async function executePipelineStepWithRetryOrNull<T>(
  operation: () => Promise<T>,
  options: PipelineRetryOptions = {}
): Promise<T | null> {
  try {
    const result = await executePipelineStepWithRetry(operation, options);
    return result.result;
  } catch (error) {
    return null;
  }
}

